import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  BlockStack,
  InlineStack,
  TextField,
  Select,
  Checkbox,
  Button,
  Text,
  Banner,
  Badge,
  Spinner,
} from '@shopify/polaris';
import { generationImportService } from '../../services/generationImportService';

const SCRIPT_OPTIONS = [
  { label: 'Mixed', value: 'mixed' },
  { label: 'Arabic', value: 'arabic' },
  { label: 'Latin', value: 'latin' },
  { label: 'Cyrillic', value: 'cyrillic' },
];

const emptyUploadState = {
  key: '',
  label: '',
  family: '',
  script: 'mixed',
  isDefault: false,
  files: {
    regular: null,
    italic: null,
    bold: null,
    bold_italic: null,
  }
};

const emptyEditState = {
  label: '',
  family: '',
  script: 'mixed',
  is_active: true,
  is_default: false,
};

const fileInputStyle = {
  width: '100%',
  border: '1px solid #c9cccf',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 13,
};

const GenerationFontLibraryModal = ({ isOpen, onClose }) => {
  const [fonts, setFonts] = useState([]);
  const [activeOnly, setActiveOnly] = useState(false);
  const [isLoadingFonts, setIsLoadingFonts] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [busyFontId, setBusyFontId] = useState('');

  const [uploadData, setUploadData] = useState(emptyUploadState);
  const [editingFontId, setEditingFontId] = useState('');
  const [editData, setEditData] = useState(emptyEditState);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const resetLocalState = () => {
    setFonts([]);
    setActiveOnly(false);
    setIsLoadingFonts(false);
    setIsUploading(false);
    setIsSavingEdit(false);
    setBusyFontId('');
    setUploadData(emptyUploadState);
    setEditingFontId('');
    setEditData(emptyEditState);
    setError('');
    setSuccess('');
  };

  const handleClose = () => {
    resetLocalState();
    onClose();
  };

  const fetchFonts = useCallback(async () => {
    setIsLoadingFonts(true);
    setError('');
    try {
      const result = await generationImportService.getFonts({ activeOnly });
      setFonts(Array.isArray(result?.data) ? result.data : []);
    } catch (err) {
      setFonts([]);
      setError(err.message || 'Failed to load font library');
    } finally {
      setIsLoadingFonts(false);
    }
  }, [activeOnly]);

  useEffect(() => {
    if (!isOpen) return;
    fetchFonts();
  }, [isOpen, fetchFonts]);

  const handleUploadFileChange = (field) => (event) => {
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    setUploadData((prev) => ({
      ...prev,
      files: {
        ...prev.files,
        [field]: file,
      }
    }));
  };

  const canUpload = useMemo(() => (
    Boolean(uploadData.key.trim()) &&
    Boolean(uploadData.label.trim()) &&
    Boolean(uploadData.family.trim()) &&
    Boolean(uploadData.files.regular)
  ), [uploadData]);

  const handleUploadFont = async () => {
    if (!canUpload) return;
    setIsUploading(true);
    setError('');
    setSuccess('');
    try {
      await generationImportService.uploadFont({
        key: uploadData.key.trim(),
        label: uploadData.label.trim(),
        family: uploadData.family.trim(),
        script: uploadData.script,
        isDefault: uploadData.isDefault,
        files: uploadData.files,
      });

      setUploadData(emptyUploadState);
      setSuccess('Font uploaded successfully');
      await fetchFonts();
    } catch (err) {
      setError(err.message || 'Failed to upload font');
    } finally {
      setIsUploading(false);
    }
  };

  const beginEdit = (font) => {
    if (!font || !font._id) return;
    setEditingFontId(font._id);
    setEditData({
      label: font.label || '',
      family: font.family || '',
      script: font.script || 'mixed',
      is_active: font.is_active !== false,
      is_default: !!font.is_default,
    });
    setError('');
    setSuccess('');
  };

  const handleSaveEdit = async () => {
    if (!editingFontId || !editData.label.trim() || !editData.family.trim()) return;
    setIsSavingEdit(true);
    setError('');
    setSuccess('');
    try {
      await generationImportService.updateFont(editingFontId, {
        label: editData.label.trim(),
        family: editData.family.trim(),
        script: editData.script,
        is_active: editData.is_active,
        is_default: editData.is_default,
      });
      setSuccess('Font updated successfully');
      setEditingFontId('');
      setEditData(emptyEditState);
      await fetchFonts();
    } catch (err) {
      setError(err.message || 'Failed to update font');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const toggleFontActive = async (font) => {
    if (!font || !font._id) return;
    setBusyFontId(font._id);
    setError('');
    setSuccess('');
    try {
      await generationImportService.updateFont(font._id, {
        is_active: font.is_active === false,
      });
      setSuccess(`Font ${font.is_active === false ? 'activated' : 'deactivated'} successfully`);
      await fetchFonts();
    } catch (err) {
      setError(err.message || 'Failed to update font status');
    } finally {
      setBusyFontId('');
    }
  };

  const setAsDefault = async (font) => {
    if (!font || !font._id || font.is_default) return;
    setBusyFontId(font._id);
    setError('');
    setSuccess('');
    try {
      await generationImportService.updateFont(font._id, { is_default: true });
      setSuccess('Default font updated successfully');
      await fetchFonts();
    } catch (err) {
      setError(err.message || 'Failed to update default font');
    } finally {
      setBusyFontId('');
    }
  };

  const handleDeleteFont = async (font) => {
    if (!font || !font._id) return;
    const confirmed = window.confirm(`Deactivate font "${font.label || font.key}"?`);
    if (!confirmed) return;

    setBusyFontId(font._id);
    setError('');
    setSuccess('');
    try {
      await generationImportService.deleteFont(font._id);
      setSuccess('Font deactivated successfully');
      if (editingFontId === font._id) {
        setEditingFontId('');
        setEditData(emptyEditState);
      }
      await fetchFonts();
    } catch (err) {
      setError(err.message || 'Failed to delete font');
    } finally {
      setBusyFontId('');
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={<div style={{ padding: '16px' }}>Generation Font Library</div>}
      primaryAction={{
        content: isUploading ? 'Uploading...' : 'Upload Font',
        onAction: handleUploadFont,
        loading: isUploading,
        disabled: isUploading || isLoadingFonts || !canUpload,
      }}
      secondaryActions={[
        {
          content: isLoadingFonts ? 'Refreshing...' : 'Refresh',
          onAction: fetchFonts,
          loading: isLoadingFonts,
        },
        {
          content: 'Close',
          onAction: handleClose,
          disabled: isUploading || isSavingEdit,
        }
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {error ? (
            <Banner tone="critical">
              <Text as="p">{error}</Text>
            </Banner>
          ) : null}

          {success ? (
            <Banner tone="success">
              <Text as="p">{success}</Text>
            </Banner>
          ) : null}

          <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
            <BlockStack gap="300">
              <Text as="p" variant="headingSm">Upload New Font</Text>

              <InlineStack gap="300" wrap>
                <div style={{ minWidth: 180, flex: 1 }}>
                  <TextField
                    label="Font Key"
                    value={uploadData.key}
                    onChange={(value) => setUploadData((prev) => ({ ...prev, key: value }))}
                    autoComplete="off"
                    placeholder="ping-lcg"
                  />
                </div>
                <div style={{ minWidth: 180, flex: 1 }}>
                  <TextField
                    label="Label"
                    value={uploadData.label}
                    onChange={(value) => setUploadData((prev) => ({ ...prev, label: value }))}
                    autoComplete="off"
                    placeholder="Ping LCG"
                  />
                </div>
                <div style={{ minWidth: 180, flex: 1 }}>
                  <TextField
                    label="Font Family"
                    value={uploadData.family}
                    onChange={(value) => setUploadData((prev) => ({ ...prev, family: value }))}
                    autoComplete="off"
                    placeholder="PingLCG"
                  />
                </div>
              </InlineStack>

              <InlineStack gap="300" wrap>
                <div style={{ minWidth: 220 }}>
                  <Select
                    label="Script"
                    options={SCRIPT_OPTIONS}
                    value={uploadData.script}
                    onChange={(value) => setUploadData((prev) => ({ ...prev, script: value }))}
                  />
                </div>
                <div style={{ minWidth: 220, paddingTop: 28 }}>
                  <Checkbox
                    label="Set as default"
                    checked={uploadData.isDefault}
                    onChange={(checked) => setUploadData((prev) => ({ ...prev, isDefault: checked }))}
                  />
                </div>
              </InlineStack>

              <InlineStack gap="300" wrap>
                <div style={{ minWidth: 220, flex: 1 }}>
                  <Text as="p" variant="bodySm" tone="subdued" style={{ marginBottom: 4 }}>Regular (required)</Text>
                  <input type="file" accept=".ttf,.otf,.woff,.woff2" onChange={handleUploadFileChange('regular')} style={fileInputStyle} />
                </div>
                <div style={{ minWidth: 220, flex: 1 }}>
                  <Text as="p" variant="bodySm" tone="subdued" style={{ marginBottom: 4 }}>Italic (optional)</Text>
                  <input type="file" accept=".ttf,.otf,.woff,.woff2" onChange={handleUploadFileChange('italic')} style={fileInputStyle} />
                </div>
              </InlineStack>

              <InlineStack gap="300" wrap>
                <div style={{ minWidth: 220, flex: 1 }}>
                  <Text as="p" variant="bodySm" tone="subdued" style={{ marginBottom: 4 }}>Bold (optional)</Text>
                  <input type="file" accept=".ttf,.otf,.woff,.woff2" onChange={handleUploadFileChange('bold')} style={fileInputStyle} />
                </div>
                <div style={{ minWidth: 220, flex: 1 }}>
                  <Text as="p" variant="bodySm" tone="subdued" style={{ marginBottom: 4 }}>Bold Italic (optional)</Text>
                  <input type="file" accept=".ttf,.otf,.woff,.woff2" onChange={handleUploadFileChange('bold_italic')} style={fileInputStyle} />
                </div>
              </InlineStack>
            </BlockStack>
          </div>

          <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
            <BlockStack gap="300">
              <InlineStack align="space-between">
                <Text as="p" variant="headingSm">Font Library</Text>
                <Checkbox
                  label="Active only"
                  checked={activeOnly}
                  onChange={(checked) => setActiveOnly(checked)}
                />
              </InlineStack>

              {isLoadingFonts ? (
                <InlineStack gap="200" align="center">
                  <Spinner size="small" />
                  <Text as="p">Loading fonts...</Text>
                </InlineStack>
              ) : fonts.length === 0 ? (
                <Text as="p" tone="subdued">No fonts found.</Text>
              ) : (
                <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #f1f1f1', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #f1f1f1' }}>Font</th>
                        <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #f1f1f1' }}>Key</th>
                        <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #f1f1f1' }}>Script</th>
                        <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #f1f1f1' }}>Status</th>
                        <th style={{ textAlign: 'right', padding: 8, borderBottom: '1px solid #f1f1f1' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fonts.map((font) => {
                        const isBusy = busyFontId === font._id;
                        const variantCount = Object.values(font.variants || {}).filter(Boolean).length;
                        return (
                          <tr key={font._id}>
                            <td style={{ padding: 8, borderBottom: '1px solid #f8f8f8' }}>
                              <Text as="p" variant="bodyMd">{font.label || '-'}</Text>
                              <Text as="p" variant="bodySm" tone="subdued">{font.family || '-'}</Text>
                            </td>
                            <td style={{ padding: 8, borderBottom: '1px solid #f8f8f8' }}>{font.key}</td>
                            <td style={{ padding: 8, borderBottom: '1px solid #f8f8f8' }}>{font.script || 'mixed'}</td>
                            <td style={{ padding: 8, borderBottom: '1px solid #f8f8f8' }}>
                              <InlineStack gap="100" wrap>
                                <Badge tone={font.is_active === false ? 'critical' : 'success'}>
                                  {font.is_active === false ? 'Inactive' : 'Active'}
                                </Badge>
                                {font.is_default ? <Badge tone="attention">Default</Badge> : null}
                                <Badge tone="info">{variantCount} variants</Badge>
                              </InlineStack>
                            </td>
                            <td style={{ padding: 8, borderBottom: '1px solid #f8f8f8', textAlign: 'right' }}>
                              <InlineStack gap="200" align="end">
                                <Button size="slim" onClick={() => beginEdit(font)} disabled={isBusy}>Edit</Button>
                                <Button size="slim" onClick={() => toggleFontActive(font)} loading={isBusy}>
                                  {font.is_active === false ? 'Activate' : 'Deactivate'}
                                </Button>
                                <Button size="slim" onClick={() => setAsDefault(font)} disabled={font.is_default || isBusy}>Set Default</Button>
                                <Button size="slim" tone="critical" onClick={() => handleDeleteFont(font)} loading={isBusy}>Delete</Button>
                              </InlineStack>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </BlockStack>
          </div>

          {editingFontId ? (
            <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
              <BlockStack gap="300">
                <Text as="p" variant="headingSm">Edit Font</Text>

                <InlineStack gap="300" wrap>
                  <div style={{ minWidth: 180, flex: 1 }}>
                    <TextField
                      label="Label"
                      value={editData.label}
                      onChange={(value) => setEditData((prev) => ({ ...prev, label: value }))}
                      autoComplete="off"
                    />
                  </div>
                  <div style={{ minWidth: 180, flex: 1 }}>
                    <TextField
                      label="Family"
                      value={editData.family}
                      onChange={(value) => setEditData((prev) => ({ ...prev, family: value }))}
                      autoComplete="off"
                    />
                  </div>
                  <div style={{ minWidth: 180, flex: 1 }}>
                    <Select
                      label="Script"
                      options={SCRIPT_OPTIONS}
                      value={editData.script}
                      onChange={(value) => setEditData((prev) => ({ ...prev, script: value }))}
                    />
                  </div>
                </InlineStack>

                <InlineStack gap="300" wrap>
                  <Checkbox
                    label="Active"
                    checked={editData.is_active}
                    onChange={(checked) => setEditData((prev) => ({ ...prev, is_active: checked }))}
                  />
                  <Checkbox
                    label="Default"
                    checked={editData.is_default}
                    onChange={(checked) => setEditData((prev) => ({ ...prev, is_default: checked }))}
                  />
                </InlineStack>

                <InlineStack gap="200">
                  <Button variant="primary" loading={isSavingEdit} onClick={handleSaveEdit}>Save Changes</Button>
                  <Button onClick={() => {
                    setEditingFontId('');
                    setEditData(emptyEditState);
                  }} disabled={isSavingEdit}>Cancel</Button>
                </InlineStack>
              </BlockStack>
            </div>
          ) : null}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default GenerationFontLibraryModal;
